import { memo, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link, useNavigate } from "@tanstack/react-router"

import { formatRelativeTime } from "@productlane/lib/src/date"
import { Button, Empty, Tabs, TabsList, TabsTrigger } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { ChatsTab } from "./assistant-chats"
import { toast } from "@/components/toast"
import { hrefLinkOptions } from "@/lib/href-link"
import { mutateOk, useZero } from "@/lib/zero"
import { BreadcrumbSlash, PageHeader } from "../page-header"
import { BreadcrumbBack } from "./agent-breadcrumb"
import {
	agentSidebarRows,
	CHATS_PAGE_SIZE,
	CHATS_TABS,
	nextChatsLimit,
	rowsForTab,
	SURFACE_LABELS,
} from "./assistant-chats"
import { FADE_OUT_LABEL } from "./idea-list"
import { useAgentItems } from "./use-agent-recommendations"

/** View all: every idea and every assistant chat the user had, by surface. */
export const AssistantChatsPage = memo(() => {
	const zero = useZero()
	const navigate = useNavigate()
	const [tab, setTab] = useState<ChatsTab>("all")
	const [limit, setLimit] = useState(CHATS_PAGE_SIZE)
	const archived = tab === "archived"
	const { items } = useAgentItems()
	const [chats, result] = useQuery(
		queries.myAssistantChats({ archived, limit }),
	)
	const nextLimit =
		result.type === "complete" ? nextChatsLimit(chats.length, limit) : null
	const rows = useMemo(
		() => rowsForTab(agentSidebarRows(archived ? [] : items, chats), tab),
		[archived, items, chats, tab],
	)

	const setArchived = (chatId: string, value: boolean) => {
		mutateOk(
			zero.mutate(
				mutators.aiChatSessions.archive({ id: chatId, archived: value }),
			),
		).catch(() =>
			toast.error({
				title: value
					? "Couldn't archive this chat"
					: "Couldn't restore this chat",
			}),
		)
	}

	return (
		<div className="flex min-h-0 flex-1 flex-col">
			<PageHeader
				title={
					<nav
						aria-label="Breadcrumb"
						className="flex min-w-0 items-center gap-2 text-sm font-normal"
					>
						<BreadcrumbBack onClick={() => void navigate({ to: "/agent" })} />
						<BreadcrumbSlash />
						<span className="min-w-0 truncate text-primary">All chats</span>
					</nav>
				}
			/>
			<div className="no-scrollbar flex flex-none items-center overflow-x-auto px-6 pb-2 pt-2">
				<Tabs
					value={tab}
					onValueChange={(next) => {
						setTab(next as ChatsTab)
						setLimit(CHATS_PAGE_SIZE)
					}}
				>
					<TabsList>
						{CHATS_TABS.map(({ id, label }) => (
							<TabsTrigger key={id} value={id}>
								{label}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</div>
			<div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
				{rows.length === 0 ? (
					<Empty
						className="mt-16"
						title={archived ? "No archived chats" : "No chats yet"}
						text={
							archived
								? "Chats you archive from the sidebar wait here."
								: "Ideas from the agent and your assistant chats show up here."
						}
					/>
				) : (
					<ul className="mx-auto flex max-w-[760px] list-none flex-col gap-px">
						{rows.map(({ chatId, ...row }) => (
							<li
								key={row.id}
								data-testid="agent-chats-row"
								className="flex items-center gap-1"
							>
								<Link
									{...hrefLinkOptions(row.href)}
									className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-primary hover:bg-gray-100 dark:hover:bg-gray-800"
								>
									<span className={FADE_OUT_LABEL}>{row.title}</span>
									<span className="flex-none text-xs text-tertiary">
										{SURFACE_LABELS[row.surface]}
									</span>
									<span className="w-16 flex-none text-right text-xs tabular-nums text-tertiary">
										{formatRelativeTime(new Date(row.at))}
									</span>
								</Link>
								{chatId && (
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setArchived(chatId, !archived)}
									>
										{archived ? "Restore" : "Archive"}
									</Button>
								)}
							</li>
						))}
						{nextLimit !== null && (
							<li className="flex justify-center pt-2">
								<Button
									variant="ghost"
									size="sm"
									data-testid="agent-chats-more"
									onClick={() => setLimit(nextLimit)}
								>
									Show more
								</Button>
							</li>
						)}
					</ul>
				)}
			</div>
		</div>
	)
})
